'use client';

import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

import { Heading } from '../ui/heading';

/**
 * Proces „Od dopytu po doručenie" — Figma desktop `44:850`, mobil `60042:4497`.
 *
 * Rozmery odmerané z Figmy:
 *   desktop  sekcia py 160 · nadpis→os 48 · pás s osou 1168 široký, vycentrovaný
 *            os 80 · krok 544 = (1168 − 80) / 2 · zvislá medzera medzi krokmi 84
 *   mobil    sekcia py 64 · nadpis→os 32 · os vľavo (16), kroky vedľa nej
 *            medzera 16 · zvislá medzera medzi krokmi 24
 *
 * Striedanie krokov vľavo/vpravo je vo Figme nakreslené ako dva stĺpce s medzerou
 * 280 a pravým odsadeným o 190. Vyjde z toho ale obyčajná postupnosť: kroky idú
 * pod sebou (01 → 02 → 03 → 04) s medzerou ~84 a striedajú stranu. Preto je to
 * jeden zoznam so `self-start` / `self-end`, nie dva stĺpce — poradie v DOM tak
 * ostáva správne pre čítačky aj pre počítanie aktívnych krokov.
 *
 * ── Ako to funguje ──────────────────────────────────────────────────────────
 * Špička modrého úseku je zarovnaná na ZVISLÝ STRED VIEWPORTU. Ako sa scrolluje,
 * putuje po osi nadol a krok sa prepne zo šedej na tmavú vo chvíli, keď špička
 * dosiahne STRED toho kroku. Pri scrollovaní späť hore sa kroky zase zošedivejú —
 * stav vždy zodpovedá polohe scrollu, nič sa nepamätá. Rast je plynulý, nie
 * skokový po krokoch.
 *
 * Čísla 01–04 sa berú z indexu pri mapovaní, nie z CMS poľa.
 *
 * Farby: jediná premenná je `primary`, zvyšok natvrdo hexom.
 */

/** Index poľa „Popis" v dynamickej sekcii Proces. */
const DESCRIPTION_FIELD = 0;

export function Process({ steps: rawSteps }: { steps: TDynamicData[] }) {
  const steps = rawSteps.map(item => ({
    id: item.id,
    title: item.name,
    description: item.fields[DESCRIPTION_FIELD]?.value ?? '',
  }));

  const axisRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<(HTMLLIElement | null)[]>([]);
  const frame = useRef<number | null>(null);

  /** Dĺžka modrého úseku v px, meraná od vrchu osi. */
  const [progress, setProgress] = useState(0);
  /** Koľko krokov od začiatku je aktívnych. */
  const [activeCount, setActiveCount] = useState(0);

  const measure = useCallback(() => {
    const axis = axisRef.current;
    if (!axis) return;

    const viewportCenter = window.innerHeight / 2;
    const axisRect = axis.getBoundingClientRect();

    // Špička = stred viewportu premietnutý do súradníc osi, orezaný na jej
    // dĺžku (nad sekciou 0, pod sekciou plná dĺžka).
    setProgress(Math.max(0, Math.min(axisRect.height, viewportCenter - axisRect.top)));

    // Krok je aktívny, keď jeho stred pretne stred viewportu. Počíta sa od
    // začiatku a pri prvom neaktívnom sa končí, aby nemohol svietiť tretí bez
    // druhého.
    let count = 0;
    for (const step of stepRefs.current) {
      if (!step) break;
      const rect = step.getBoundingClientRect();
      if (rect.top + rect.height / 2 > viewportCenter) break;
      count += 1;
    }
    setActiveCount(count);
  }, []);

  useEffect(() => {
    // Scroll prichádza hustejšie než prekresľovanie, tak sa meria raz za snímku.
    const onScroll = () => {
      if (frame.current !== null) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        measure();
      });
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, [measure]);

  if (!steps.length) return null;

  return (
    <section
      id="proces"
      className={cn(
        'custom-section',
        'py-16 sm:py-20 md:py-24 lg:py-32 xl:py-40',
        // Kotva z menu (#proces) nesmie skončiť pod fixnou hlavičkou.
        'scroll-mt-[58px] lg:scroll-mt-[100px]',
      )}
    >
      <div
        className={cn('custom-container', 'flex flex-col items-center gap-8 md:gap-10 lg:gap-12')}
      >
        <Heading
          tag="h2"
          center
          subtitle="Ako to prebieha"
          title="Od dopytu po doručenie"
          description="Štyri kroky, ktoré medzi vaším dopytom a doručením zásielky stoja. Bez zbytočného čakania a bez skrytých poplatkov."
        />

        <div className={cn('relative flex w-full gap-4', 'lg:mx-auto lg:block lg:max-w-[1168px]')}>
          {/* Os. Na mobile stojí v toku vľavo, od lg je absolútne v strede pásu
              a kroky sa striedajú okolo nej. V oboch prípadoch je vysoká presne
              ako celý zoznam, takže sa dá merať tým istým refom. */}
          <div
            ref={axisRef}
            aria-hidden="true"
            className={cn(
              'relative w-4 shrink-0',
              'lg:absolute lg:inset-y-0 lg:left-1/2 lg:w-20 lg:-translate-x-1/2',
            )}
          >
            <span className="absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 rounded-pill bg-[#E7ECF3]" />
            <span
              className="absolute left-1/2 top-0 w-1 -translate-x-1/2 rounded-pill bg-[#5B9BFF]"
              style={{ height: progress }}
            />
          </div>

          <ol
            className={cn(
              'flex min-w-0 flex-1 list-none flex-col gap-6',
              'lg:w-full lg:gap-16 xl:gap-[84px]',
            )}
          >
            {steps.map((step, index) => {
              const isActive = index < activeCount;
              const isLeft = index % 2 === 0;

              return (
                <li
                  key={step.id}
                  ref={element => {
                    stepRefs.current[index] = element;
                  }}
                  className={cn(
                    'flex items-start gap-4 lg:gap-8',
                    // Figma: krok je 544 zo šírky 1168, teda polovica mínus
                    // polovica osi (80 / 2 = 40).
                    'lg:w-[calc(50%-2.5rem)]',
                    isLeft ? 'lg:self-start' : 'lg:self-end',
                  )}
                >
                  {/* Číslo ostáva tlmené aj pri aktívnom kroku — vo Figme sa
                      mení len nadpis a popis. */}
                  <Heading
                    tag="h4"
                    as="p"
                    title={String(index + 1).padStart(2, '0')}
                    className={{
                      container: 'w-auto shrink-0',
                      title: 'text-[#A9B7CC]',
                    }}
                  />

                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Heading
                      tag="h3"
                      title={step.title}
                      className={{
                        title: cn(
                          'transition-colors duration-300',
                          isActive ? 'text-[#212121]' : 'text-[#A9B7CC]',
                        ),
                      }}
                    />
                    <p
                      className={cn(
                        'text-[16px] leading-[1.3] tracking-[-0.031em] lg:leading-[1.5]',
                        'transition-colors duration-300',
                        isActive ? 'text-[#212121]' : 'text-[#A9B7CC]',
                      )}
                    >
                      {step.description}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
